import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import {
  atxIsMobile$,
  atxIsScreenLg$,
} from '@epicuro-next/ataraxia/breakpoint';
import { faSettings } from '@epicuro-next/ataraxia/icons';
import {
  SpritesPaths,
  SPRITES_PATHS_TOKEN,
} from '@epicuro-next/ataraxia/models';
import { SegmentMenuButton } from '@epicuro-next/ataraxia/segment-buttons';
import { presetFilterValuesLocales } from '@epicuro-next/constants/features/localization';
import { selectIsFiltersVisibleRule } from '@epicuro-next/platform/configuration';
import { selectActiveAccountCurrency } from '@epicuro-next/state/accounts-state';
import {
  clearFilters,
  loadDefaultFilters,
  loadFilters,
  openFiltersModal,
  selectActiveFilters,
  selectActiveFiltersCount,
  selectFilterShortcutConfig,
  selectPresetDisplayName,
  selectPresetValue,
  selectProductsLoading,
  selectResultCount,
  selectSearchBarSegmentButton,
  toggleFilter,
} from '@epicuro-next/state/p-basket-state';
import { recommendationUrlRedirect } from '@epicuro-next/state/recommendation-panels-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

import { SearchFiltersContainerComponent } from '../search-filters-container/search-filters-container.component';

const orderTypeButtonsConfigurationLocale: Record<string, string> = {
  SEARCH_BAR_ORDER_TYPE_NOW_LOCALE_KEY: presetFilterValuesLocales.now,
  SEARCH_BAR_ORDER_TYPE_FUTURE_LOCALE_KEY: presetFilterValuesLocales.future,
};

@Component({
  selector: 'epc-search-filters',
  templateUrl: './search-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./search-filters.component.scss'],
  standalone: false,
})
export class SearchFiltersComponent implements OnInit, OnDestroy {
  @Output() public openSearch = new EventEmitter();

  @ViewChild(SearchFiltersContainerComponent)
  public filtersContainer: SearchFiltersContainerComponent;

  public isMobile$ = atxIsMobile$;

  public readonly show$$ = new BehaviorSubject(false);
  private destroy$ = new Subject<void>();

  public filterIcon = faSettings;

  /* Total products result */
  public resultCount$ = this.store.pipe(select(selectResultCount));

  /** Products loading state */
  public productsLoading$ = this.store.pipe(select(selectProductsLoading));

  public totalFilters$ = this.store.pipe(select(selectActiveFiltersCount));
  public presetDisplayName$ = this.store.pipe(select(selectPresetDisplayName));

  /** Active filters values */
  public activeFilters$ = this.store.pipe(select(selectActiveFilters));

  /* Active Account Currency */
  public activeAccountCurrency$ = this.store.pipe(
    select(selectActiveAccountCurrency),
  );

  public filterShortcutConfig$ = this.store.pipe(
    select(selectFilterShortcutConfig),
  );

  public isFiltersVisibleEnabledRule = toSignal(
    this.store.select(selectIsFiltersVisibleRule),
  );

  public atxIsScreenLg = toSignal(atxIsScreenLg$);

  public showFiltersButton = computed(() => {
    return !this.isFiltersVisibleEnabledRule() || this.atxIsScreenLg();
  });

  public orderTypeControl = new FormControl<string>('');
  public orderTypeConfiguration = this.store.selectSignal(
    selectSearchBarSegmentButton,
  );

  public localizedOrderTypeConfiguration = computed(() =>
    this.orderTypeConfiguration()?.map((button: SegmentMenuButton) => ({
      value: button.value,
      displayValue:
        orderTypeButtonsConfigurationLocale[button.displayValue] ??
        button.displayValue,
    })),
  );

  public showOrderTypeButtons = computed(
    () =>
      this.localizedOrderTypeConfiguration()?.length > 0 &&
      !this.atxIsScreenLg(),
  );

  constructor(
    @Inject(SPRITES_PATHS_TOKEN) public spritesPaths: SpritesPaths,
    private store: Store,
    private actions$: Actions,
    private router: Router,
  ) {}

  /**
   * Function to remove all filters from the query
   */
  public resetAll() {
    this.store.dispatch(clearFilters({ skipParams: false }));
    this.refreshAccordions();
  }

  public ngOnInit() {
    this.store.dispatch(loadFilters());
    this.store.dispatch(loadDefaultFilters());

    this.listenToOpenFiltersModal();

    this.store
      .pipe(
        select(selectPresetValue),
        filter(isNonNullish),
        takeUntil(this.destroy$),
      )
      .subscribe((preset) => {
        this.orderTypeControl.setValue(preset, {
          emitEvent: false,
        });
      });

    this.orderTypeControl.valueChanges
      .pipe(
        tap((value: string) => {
          this.store.dispatch(
            toggleFilter({
              fieldId: 'preset',
              value,
            }),
          );

          this.openSearch.emit();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toggleModal(show: boolean = false) {
    this.show$$.next(show);
  }

  public showProducts() {
    this.openSearch.emit();
    this.toggleModal(false);
  }

  public goToShortcut(url: string) {
    this.router.navigateByUrl(url);
    this.store.dispatch(
      recommendationUrlRedirect({
        panelName: 'FiltersShortcutButton',
        providerId: 'ProductSearch',
        panelStyle: 'SCB',
        routerLink: url,
      }),
    );
  }

  public refreshAccordions() {
    this.filtersContainer.refreshAccordions();
  }

  private listenToOpenFiltersModal() {
    this.actions$
      .pipe(ofType(openFiltersModal), takeUntil(this.destroy$))
      .subscribe(() => this.toggleModal(true));
  }
}
